import { computed } from 'vue';
import { useDossierStore } from '@frontend/ui/stores/dossierStore';
import { prettifyMixedCase } from '@frontend/shared/Util.js';

const getAllPathsAsArrays = (obj, currentPath = []) => {
  let paths = [];

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue; // Skip inherited properties
    if (key === 'metadata') continue; // Skip metadata

    const newPath = [...currentPath.flat(), key];
    const value = obj[key];

    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((_, index) => {
          paths = paths.concat(
              getAllPathsAsArrays(value[index], [newPath, index]),
          );
        });
      } else {
        paths = paths.concat(getAllPathsAsArrays(value, [newPath]));
      }
    } else if (value !== null) {
      paths.push(newPath);
    }
  }

  return paths;
};

const getValueFromArrayPath = (obj, path) => {
  return path.reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
      obj,
  );
};

export function useDossierItems(draftDossierRef) {
  const dossierStore = useDossierStore();

  const currentDossier = computed(() => dossierStore.currentValue);
  const currentPaths = computed(() =>
    getAllPathsAsArrays(currentDossier.value),
  );
  const draftPaths = computed(() => getAllPathsAsArrays(draftDossierRef.value));

  const allItems = computed(() => {
    const processedPaths = [];

    const fromCurrent = currentPaths.value.map((path) => {
      const id = path.join('_');
      processedPaths.push(id);

      const currentValue = getValueFromArrayPath(currentDossier.value, path);
      const draftValue = getValueFromArrayPath(draftDossierRef.value, path);

      if (
        (currentValue && draftValue && currentValue === draftValue) ||
        (!currentValue && !draftValue)
      ) {
        return;
      }

      let type = 'unchanged';
      if (!currentValue && draftValue) type = 'new value';
      else if (currentValue && draftValue && currentValue !== draftValue) {
        type = 'change';
      } else if (!draftValue) type = 'removal';

      return {
        id,
        type,
        path,
        depth: path.length - 1,
        location: prettifyMixedCase(path.at(-1)),
        currentValue,
        draftValue,
      };
    });

    const fromDraft = draftPaths.value.map((path) => {
      const id = path.join('_');
      if (processedPaths.includes(id)) return;

      const currentValue = getValueFromArrayPath(currentDossier.value, path);
      const draftValue = getValueFromArrayPath(draftDossierRef.value, path);

      let type = 'new value';
      if (
        (currentValue && draftValue && currentValue === draftValue) ||
        (!currentValue && !draftValue)
      ) {
        type = 'unchanged';
      }

      return {
        id,
        type,
        path,
        depth: path.length - 1,
        location: prettifyMixedCase(path.at(-1)),
        currentValue,
        draftValue,
      };
    });

    return [...fromCurrent, ...fromDraft].filter(Boolean);
  });

  const changedItems = computed(() => {
    return allItems.value.filter((item) => item.type !== 'unchanged');
  });

  return {
    currentDossier,
    currentPaths,
    draftPaths,
    allItems,
    changedItems,
  };
}
